import { useState } from 'react'
import { ChevronsUpDown, X, Check } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { cn } from '@/lib/utils'

export interface ComboboxOption<TValue extends string | number> {
  value: TValue
  label: string
  /** Optional element rendered next to label (np. ikona z lucide). */
  prefix?: React.ReactNode
  /** Disabled opcja - widoczna ale nie wybieralna. */
  disabled?: boolean
}

interface ComboboxProps<TValue extends string | number> {
  value: TValue | null
  onChange: (v: TValue | null) => void
  options: ComboboxOption<TValue>[]
  placeholder?: string
  /** Tekst dla opcji "brak wyboru" - jeśli pominięte, brak opcji null (lub clear button). */
  emptyOptionLabel?: string
  /** Schowaj search input gdy opcji <= tej wartości. Default 8. */
  searchThreshold?: number
  className?: string
  disabled?: boolean
}

/**
 * Generic searchable combobox - zastępuje natywny <select> wszędzie gdzie potrzeba
 * konsistentnego ładnego stylingu. Wzorzec wzięty z HaloUserCombobox (async user search).
 *
 * Sync variant: przekazujesz pełną listę przez `options`. Search filtruje lokalnie.
 * Dla async (search-driven) - dedykowane komponenty per zasób (np. HaloUserCombobox).
 *
 * Convention: gdy opcji <= 8 (`searchThreshold`), search input ukryty - kliknij i widzisz
 * pełną listę. Dla 9+ opcji input pokazuje się + filtruje case-insensitive na label.
 */
export function Combobox<TValue extends string | number>({
  value,
  onChange,
  options,
  placeholder = 'Wybierz...',
  emptyOptionLabel,
  searchThreshold = 8,
  className,
  disabled,
}: ComboboxProps<TValue>) {
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')

  const selected = options.find((o) => o.value === value) ?? null
  const showSearch = options.length > searchThreshold

  const filtered = !search
    ? options
    : options.filter((o) => o.label.toLowerCase().includes(search.toLowerCase()))

  const handleSelect = (v: TValue | null) => {
    onChange(v)
    setOpen(false)
    setSearch('')
  }

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation()
    onChange(null)
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          disabled={disabled}
          aria-expanded={open}
          className={cn('w-full justify-between font-normal', className)}
        >
          <span className={cn('flex items-center gap-1.5 whitespace-nowrap', !selected && 'text-slate-400')}>
            {selected?.prefix}
            {selected?.label ?? placeholder}
          </span>
          <span className="flex items-center gap-1 shrink-0">
            {value != null && (
              <span
                role="button"
                tabIndex={0}
                onClick={handleClear}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') handleClear(e as unknown as React.MouseEvent)
                }}
                className="rounded p-0.5 hover:bg-muted"
                aria-label="Wyczyść"
              >
                <X className="size-3.5" />
              </span>
            )}
            <ChevronsUpDown className="size-4 opacity-50" />
          </span>
        </Button>
      </PopoverTrigger>
      <PopoverContent portal={false} className="min-w-[var(--radix-popover-trigger-width)] p-0" align="start">
        {showSearch && (
          <div className="p-2 border-b">
            <Input
              autoFocus
              placeholder="Szukaj..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="h-8 text-sm"
            />
          </div>
        )}
        <ul className="max-h-64 overflow-y-auto py-1">
          {emptyOptionLabel && (
            <li>
              <button
                type="button"
                onClick={() => handleSelect(null)}
                className="w-full text-left px-3 py-1.5 text-sm text-slate-500 hover:bg-slate-100 flex items-center gap-2"
              >
                <span className="size-4 shrink-0" />
                {emptyOptionLabel}
              </button>
            </li>
          )}
          {filtered.length === 0 && (
            <li className="px-3 py-2 text-xs text-slate-400 italic">Brak wyników</li>
          )}
          {filtered.map((opt) => (
            <li key={String(opt.value)}>
              <button
                type="button"
                disabled={opt.disabled}
                onClick={() => handleSelect(opt.value)}
                className={cn(
                  'w-full text-left px-3 py-1.5 text-sm hover:bg-slate-100 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed',
                  opt.value === value && 'bg-sky-50',
                )}
              >
                <Check
                  className={cn('size-4 shrink-0', opt.value === value ? 'opacity-100' : 'opacity-0')}
                />
                {opt.prefix}
                <span className="whitespace-nowrap">{opt.label}</span>
              </button>
            </li>
          ))}
        </ul>
      </PopoverContent>
    </Popover>
  )
}
